import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useAuth } from '@/shared/lib/auth/auth-context'
import { onAccessSuspended } from '@/shared/lib/auth/access-suspended'

/**
 * Blocking notice for a 403 `UserAccessSuspended` (UC-084.3 E2): offers the
 * caller's other tenants and a sign-out. It never logs out on its own.
 */
export function AccessSuspendedDialog() {
  const { t } = useTranslation()
  const { user, switchTenant, isSwitchingTenant, logout, accessSuspended } = useAuth()
  const [notified, setNotified] = useState(false)
  // The bootstrap refusal arrives before this dialog mounts, so it comes as a flag, not an event.
  const open = notified || accessSuspended

  useEffect(() => onAccessSuspended(() => setNotified(true)), [])

  const otherTenants = user?.tenants.filter((tenant) => tenant.id !== user.organizationId) ?? []

  async function handleSwitch(tenantId: number) {
    try {
      await switchTenant(tenantId)
      setNotified(false)
    } catch {
      toast.error(t('tenant.switchFailed'))
    }
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('accessSuspended.title')}</DialogTitle>
          <DialogDescription>{t('accessSuspended.description')}</DialogDescription>
        </DialogHeader>
        {otherTenants.length > 0 && (
          <div className="flex flex-col gap-1">
            {otherTenants.map((tenant) => (
              <Button
                key={tenant.id}
                variant="outline"
                className="justify-start gap-2"
                disabled={isSwitchingTenant}
                onClick={() => void handleSwitch(tenant.id)}
              >
                <Building2 className="h-4 w-4" />
                {tenant.name}
              </Button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => void logout()}>{t('accessSuspended.signOut')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
